/**
 * authPolicy — pure, dependency-free auth-resilience helpers shared by api.js,
 * auth-context.jsx and the recording flow. Kept free of axios/DOM imports so it
 * is unit-testable in isolation (see __tests__/authPolicy.test.js).
 *
 * Why this exists (Phase: auth hardening "a"):
 *  - A 401 must NOT hard-logout the user (and lose an in-progress interview
 *    recording). We retry transient 401s and, while a recording is active,
 *    NEVER navigate away — we surface an honest failure instead.
 *  - A returning user's tab-refocus re-check must tolerate a transient 401
 *    rather than nuking a valid 7-day session.
 */

// ---- Recording-active flag ------------------------------------------------
// Set at recording START; cleared only after finalize is SERVER-CONFIRMED
// (or on discard/close). While true, the 401 handler refuses to hard-logout.
let _recordingActive = false;

export function setRecordingActive(active) {
  _recordingActive = !!active;
}

export function isRecordingActive() {
  return _recordingActive;
}

// ---- Token freshness (pre-flight prevention) ------------------------------
// If a recording starts with less than this much access-token life remaining,
// prompt a re-sign-in first (prevention over recovery). 2.5h comfortably
// exceeds the 120-min hard recording cap.
export const TOKEN_FRESHNESS_THRESHOLD_SECONDS = 2.5 * 60 * 60; // 9000s

export function decodeJwtPayload(token) {
  try {
    if (!token || typeof token !== "string") return null;
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const json = atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/**
 * Seconds of life left in a JWT access token, or null if the token isn't a
 * decodable JWT with an `exp` (e.g. an Emergent session cookie — we can't
 * introspect those client-side, so we don't block on them).
 */
export function tokenSecondsRemaining(token, nowMs = Date.now()) {
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== "number") return null;
  return Math.floor(payload.exp - nowMs / 1000);
}

export function isTokenTooStaleToRecord(token, nowMs = Date.now()) {
  const remaining = tokenSecondsRemaining(token, nowMs);
  // null → not an introspectable JWT → don't block (can't tell).
  if (remaining === null) return false;
  return remaining < TOKEN_FRESHNESS_THRESHOLD_SECONDS;
}

// ---- 401 decision ---------------------------------------------------------
// Pure routing of what to do on a 401, given context. The caller performs the
// side effects (refresh, retry, invalidate).
//   "propagate"             → reject as-is (auth endpoint, or already a retry)
//   "retry-then-defer"      → recording active: refresh+retry; if exhausted,
//                             DO NOT navigate — surface honestly (defer logout)
//   "retry-then-invalidate" → normal: refresh+retry once; if exhausted, logout
export function decide401Action({ isAuthUrl, recordingActive, alreadyRetried }) {
  if (isAuthUrl || alreadyRetried) return "propagate";
  if (recordingActive) return "retry-then-defer";
  return "retry-then-invalidate";
}

// True when an error is a genuine auth failure (expired/invalid session) rather
// than a server/network error. Used by the recording flow to route a failed
// finalize into the honest "stored on this device — re-sign in to recover"
// stage instead of a generic processing error.
export function isAuthFailure(err) {
  return err?.response?.status === 401;
}
